/*
 * JapansPrime: hover preview for variant options (desktop, mouse only).
 * Moving the mouse over a variant option previews that variant's photo over the
 * product gallery and its name in the option label. Clicking still selects it.
 * The preview is a throwaway overlay, so no gallery element is moved or edited.
 */
(() => {
  if (window.jpVariantHover) return;
  window.jpVariantHover = true;

  const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
  const LABEL_SELECTOR = 'variant-selects label[for]';
  const FADE_MS = 140;
  const COMMIT_TIMEOUT_MS = 1800;

  let overlay = null;
  let removeTimer = null;
  let legendState = null;
  let committing = false;
  let showToken = 0;
  const preloadedMediaIds = new Set();

  const canHover = () => window.matchMedia(HOVER_QUERY).matches;

  const inputForLabel = (label) => {
    const input = document.getElementById(label.htmlFor);
    return input && input.type === 'radio' ? input : null;
  };

  const galleryFor = (variantSelects) => {
    const scope = variantSelects.closest('product-info') || document;
    return scope.querySelector('media-gallery[data-context="product"]');
  };

  const sourceImageFor = (gallery, mediaId) =>
    gallery.querySelector(`.product__media-item[data-media-id="${mediaId}"] img`);

  const buildOverlay = () => {
    const image = document.createElement('img');
    image.className = 'jp-variant-preview';
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.decoding = 'async';
    Object.assign(image.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      objectPosition: '50% 0',
      zIndex: '6',
      pointerEvents: 'none',
      opacity: '0',
      transition: `opacity ${FADE_MS}ms ease`,
      background: 'rgb(var(--color-background, 255 255 255))'
    });
    return image;
  };

  const probeImage = (source) => {
    const probe = new Image();
    probe.sizes = source.sizes;
    probe.srcset = source.srcset;
    probe.src = source.getAttribute('src');
    return probe;
  };

  const preload = (variantSelects, gallery) => {
    variantSelects.querySelectorAll('input[data-preview-media-id]').forEach((input) => {
      const mediaId = input.dataset.previewMediaId;
      if (preloadedMediaIds.has(mediaId)) return;
      preloadedMediaIds.add(mediaId);
      const source = sourceImageFor(gallery, mediaId);
      if (source) probeImage(source);
    });
  };

  const whenLoaded = (source, callback) => {
    const probe = probeImage(source);
    if (probe.complete && probe.naturalWidth > 0) {
      callback();
      return;
    }
    probe.decode().then(callback, () => {});
  };

  const restoreLegend = () => {
    if (legendState && legendState.legend.isConnected) {
      legendState.legend.textContent = legendState.text;
    }
    legendState = null;
  };

  const previewLegend = (fieldset, text) => {
    const legend = fieldset && fieldset.querySelector('[data-selected-swatch-value]');
    if (!legend) return;
    if (!legendState || legendState.legend !== legend) {
      restoreLegend();
      legendState = { legend, text: legend.textContent };
    }
    legend.textContent = text;
  };

  const removeOverlayNow = () => {
    clearTimeout(removeTimer);
    if (!overlay) return;
    overlay.style.opacity = '0';
    overlay.remove();
  };

  const fadeOutOverlay = () => {
    if (!overlay || !overlay.parentElement) return;
    overlay.style.opacity = '0';
    clearTimeout(removeTimer);
    removeTimer = setTimeout(removeOverlayNow, FADE_MS + 20);
  };

  const paintOverlay = (host, source) => {
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    overlay = overlay || buildOverlay();
    clearTimeout(removeTimer);
    const wasDetached = !overlay.parentElement;
    if (wasDetached) host.appendChild(overlay);

    const frame = source.closest('.product__media-item-container');
    overlay.style.borderRadius = getComputedStyle(frame || source).borderRadius;
    overlay.sizes = source.sizes;
    overlay.srcset = source.srcset;
    overlay.src = source.getAttribute('src');
    if (wasDetached) void overlay.offsetWidth;
    overlay.style.opacity = '1';
  };

  const hidePreview = () => {
    showToken += 1;
    if (committing) return;
    restoreLegend();
    fadeOutOverlay();
  };

  const showPreview = (label) => {
    if (committing || !canHover()) return;
    const input = inputForLabel(label);
    const variantSelects = label.closest('variant-selects');
    const gallery = variantSelects && galleryFor(variantSelects);
    if (!input || !gallery) return;

    preload(variantSelects, gallery);

    const mediaId = input.dataset.previewMediaId;
    const fieldset = label.closest('fieldset');
    const checked = fieldset && fieldset.querySelector('input[type="radio"]:checked');
    const source = mediaId && sourceImageFor(gallery, mediaId);
    const alreadyShown = checked && checked.dataset.previewMediaId === mediaId;
    if (!source || alreadyShown) {
      hidePreview();
      return;
    }

    const host = gallery.closest('.product__media-wrapper') || gallery.parentElement;
    if (!host) return;

    showToken += 1;
    const token = showToken;
    previewLegend(fieldset, input.value);
    whenLoaded(source, () => {
      if (token === showToken && !committing) paintOverlay(host, source);
    });
  };

  const commitSelection = (variantSelects) => {
    showToken += 1;
    legendState = null;
    if (!overlay || !overlay.parentElement) return;
    committing = true;

    const gallery = galleryFor(variantSelects);
    const mediaList = gallery && gallery.querySelector('[id^="Slider-Gallery"]');
    let observer = null;
    let timeout = null;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      if (observer) observer.disconnect();
      clearTimeout(timeout);
      committing = false;
      fadeOutOverlay();
    };

    if (mediaList) {
      observer = new MutationObserver(finish);
      observer.observe(mediaList, { childList: true });
    }
    timeout = setTimeout(finish, COMMIT_TIMEOUT_MS);
  };

  document.addEventListener('mouseover', (event) => {
    if (!event.target.closest) return;
    const label = event.target.closest(LABEL_SELECTOR);
    if (label) {
      showPreview(label);
      return;
    }
    const variantSelects = event.target.closest('variant-selects');
    const gallery = variantSelects && canHover() && galleryFor(variantSelects);
    if (gallery) preload(variantSelects, gallery);
  });

  document.addEventListener('mouseout', (event) => {
    const label = event.target.closest && event.target.closest(LABEL_SELECTOR);
    if (!label) return;
    const next = event.relatedTarget;
    if (next && label.contains(next)) return;
    if (next && next.closest && next.closest(LABEL_SELECTOR)) return;
    hidePreview();
  });

  document.addEventListener(
    'change',
    (event) => {
      const variantSelects = event.target.closest && event.target.closest('variant-selects');
      if (variantSelects) commitSelection(variantSelects);
    },
    true
  );

  window.addEventListener('blur', hidePreview);
})();
